import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { API_ENDPOINT } from '@shared/constants/api-endpoints';
import { selectPushNotifications, selectUserInfo } from '@shared/store/app/app.selectors';
import { isCapacitorNative } from '@utils/capacitor-utils';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFetch } from './useFetch.ts';

export const usePushNotifications = () => {
  const [pushNotificationToken, setPushNotificationToken] = useState<string | null>(null);
  const [deviceAndUserInfo, setDeviceAndUserInfo] = useState<null | {
    fcmDeviceToken: string;
    deviceId: string;
    platform: 'ios' | 'android' | 'web';
    model: string;
    osVersion: string;
    manufacturer: string;
    isVirtual: boolean;
    webViewVersion: string;
    appVersion: string;
    sdkVersion: number | undefined;
    partyRoleId: number;
  }>(null);

  const userInfo = useSelector(selectUserInfo);

  const pushNotifications = useSelector(selectPushNotifications);
  const { hasPensions, hasContributions, hasDebts } = pushNotifications.topics;

  const { post: registerDevice } = useFetch(API_ENDPOINT.PUSH_NOTIFICATIONS_DEVICES);
  const { post: topicSubscriptions } = useFetch(API_ENDPOINT.PUSH_NOTIFICATIONS_TOPIC_SUBSCRIPTIONS);

  const appVersion = import.meta.env.APP_VERSION;

  useEffect(() => {
    if (!isCapacitorNative()) return;

    if (!userInfo) return;

    const registerNotifications = async () => {
      console.log('Initializing Push Notifications. Registering notifications...');

      const permissionResult = await PushNotifications.requestPermissions();

      if (permissionResult.receive === 'granted') {
        await PushNotifications.register();
      }

      PushNotifications.addListener('registration', async (token) => {
        setPushNotificationToken(token.value);

        // installation id, changes when the app is uninstalled and reinstalled
        const deviceId = (await Device.getId()).identifier;
        const { platform, model, osVersion, manufacturer, isVirtual, webViewVersion, androidSDKVersion, iOSVersion } =
          await Device.getInfo();

        const deviceAndUserData = {
          fcmDeviceToken: token.value,
          deviceId,
          platform,
          model,
          osVersion,
          manufacturer,
          isVirtual,
          webViewVersion,
          appVersion,
          sdkVersion: androidSDKVersion || iOSVersion,
          partyRoleId: userInfo.partyRoleType.partyRoleId
        };

        setDeviceAndUserInfo(deviceAndUserData);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Notification Received:', JSON.stringify(notification, null, 2));
      });
    };

    registerNotifications();
  }, [appVersion, userInfo]);

  useEffect(() => {
    if (!deviceAndUserInfo) return;
    if (!userInfo) return;

    let didRun = false;

    const saveDeviceAndUserInfo = async () => {
      if (didRun) return;
      didRun = true;

      // insured avaialble topics for push notifications
      const topics = {
        hasPensions,
        hasContributions,
        hasDebts,
        partyRoleId: userInfo.partyRoleType.partyRoleId
      };

      try {
        await registerDevice({ payload: deviceAndUserInfo });

        await topicSubscriptions({ payload: topics });
      } catch (err) {
        console.error('Failed to save device info or subscribe to topics:', JSON.stringify(err, null, 2));
      }
    };

    saveDeviceAndUserInfo();
  }, [deviceAndUserInfo, hasPensions, hasContributions, hasDebts, userInfo, registerDevice, topicSubscriptions]);

  return { pushNotificationToken };
};
